"use client";

import { createElement } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { getCategoryName, getPromotedCategories } from "@/config/categories";
import { PROMOTED_TILE_ART } from "@/config/promoted-tiles";
import { getCategoryPhosphorIcon } from "@/components/home/variants/categoryPhosphorIcons";
import { DragScrollRow } from "@/components/search-bar/parts";
import type { Locale } from "@/i18n/routing";

type LocaleKey = Locale;

/**
 * 추천 카테고리 한 줄(10-15) — 검색바 "무엇을" 칸과 카테고리 시트가 같이 쓴다. 주황 원 안 3D 아트
 * + 짧은 라벨, 좌우 드래그. 선택 판정·클릭 처리는 호출부 몫(검색바 = 초안 토글, 시트 = 임시 선택
 * 또는 이동).
 */
export function PromotedCategoryRow({
  mobile,
  isSelected,
  onSelect,
  wrapperClassName,
  className,
}: {
  mobile: boolean;
  isSelected: (code: string) => boolean;
  onSelect: (code: string) => void;
  wrapperClassName?: string;
  className?: string;
}) {
  const t = useTranslations("searchComposer");
  const locale = useLocale();
  const circle = mobile ? 54 : 56;

  return (
    <DragScrollRow wrapperClassName={wrapperClassName} className={cn("gap-1 pb-1 pt-1", className)}>
      {getPromotedCategories().map((category) => {
        const art = PROMOTED_TILE_ART[category.code];
        const label = art
          ? t(`promotedShort.${art.shortKey}`)
          : getCategoryName(category, locale as LocaleKey);
        const on = isSelected(category.code);
        return (
          <button
            key={category.code}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(category.code)}
            className={cn(
              "group flex shrink-0 cursor-pointer flex-col items-center gap-1.5",
              mobile ? "w-17" : "w-16",
            )}
          >
            <span
              style={{ width: circle, height: circle }}
              className={cn(
                "flex items-center justify-center rounded-full bg-orange-50 p-1 text-orange-600 transition duration-200",
                "group-hover:-translate-y-0.5 group-hover:bg-orange-100 group-hover:shadow-[0_6px_14px_-8px_rgba(234,88,12,0.55)]",
                "group-active:scale-95 motion-reduce:transition-none motion-reduce:group-hover:translate-y-0",
                on && "ring-2 ring-orange-500",
              )}
            >
              {art ? (
                <Image
                  src={art.src}
                  alt=""
                  width={80}
                  height={80}
                  // 메인 카테고리 타일과 같은 보정 — 주황 원 위에서 물 빠져 보이지 않게(10-15).
                  className="size-full object-contain saturate-[1.25] contrast-[1.06] transition-transform duration-200 group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                  // 로컬 /public 자산 — 정적 원본 서빙(메인 타일과 같음, 이미지 최적화 과금 제외).
                  unoptimized
                />
              ) : (
                createElement(getCategoryPhosphorIcon(category.code), {
                  size: mobile ? 25 : 26,
                  weight: "duotone",
                })
              )}
            </span>
            <span
              className={cn(
                "line-clamp-2 w-full text-center transition-colors",
                // 한국어만 어절 단위 — 일본어·중국어는 keep-all 이면 칸을 넘쳐 잘린다.
                locale === "ko" ? "break-keep" : "wrap-break-word",
                mobile ? "text-[13px] leading-4" : "text-[15px] leading-5",
                on ? "font-semibold text-gray-900" : "font-medium text-gray-900",
              )}
            >
              {label}
            </span>
          </button>
        );
      })}
    </DragScrollRow>
  );
}
